import type { Card } from "@amzn/ads-blink-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { TextButton } from "@amzn/storm-ui";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { CREATE_INSIGHT_TRIGGER_TEXT } from "../../../../config/insights.config";
import { InsightFamilyNameToLabelMap } from "../../../../constants/filters";
import { InsightFamilyMetadataAttributes } from "../../../../constants/insightFamily";
import { TELEMETRY_ID } from "../../../../constants/telemetry";
import type { CardWithRecommendation } from "../../../../hooks/aiRecommendedInsights/useAiRecommendedInsightsLoader";
import { useContentCardShown } from "../../../../hooks/misc/useContentCardShown";
import type { KeyValue } from "../../../../types/common";
import { insightTypeLabelToValue } from "../../../../types/filters/unified";
import type { InsightFamilyName } from "../../../../types/providers";
import {
    convertCardToPitchedContent,
    getPitchedContentDetails,
} from "../../../../utils/pitchedContentDetails.utils";
import {
    getUnifiedFilterTelemetryDetails,
    logWithTakt,
} from "../../../../utils/telemetry.utils";
import { buildSidePanelDetails } from "../../../../utils/unifiedContent.utils";
import { getSearchParams } from "../../../../utils/url.utils";
import { StyledActionsContainer } from "../../../common/styled.components";
import { CardLayoutV3 } from "../insightDetails/CardLayoutV3";
import { InsightFamilyCardAction } from "../insightFamilies/InsightFamilyCardAction";
import { MarketingNarrativeCardAction } from "../marketingNarratives/MarketingNarrativeCardAction";
import { PitchedContentCardAction } from "../pitchedContents/PitchedContentCardAction";

export interface UnifiedContentCardProps {
    content: Card | CardWithRecommendation;
    index: number;
    tag?: string;
}

export const UnifiedContentCard: React.FunctionComponent<
    UnifiedContentCardProps
> = ({ content, index, tag }) => {
    const {
        identifier,
        details,
        primaryContext,
        secondaryContext,
        lastModifiedDate,
        additionalInfo,
        cardHeader,
        title,
    } = content;

    const insightType: InsightType = content.insightType as InsightType;
    const navigate = useNavigate();

    // Log to TAKT that we showed this card
    useContentCardShown({
        insightType,
        identifier,
    });

    const handleOnDemandInsightCardClick = useCallback(() => {
        const filters = getUnifiedFilterTelemetryDetails();
        logWithTakt(TELEMETRY_ID.VIEW_INSIGHT_DETAILS_CLICK, {
            ...filters,
            identifier,
            insightType,
            resultPosition: index,
        });
        navigate(
            `/domains/${details?.domainId}/templates/${identifier}${getSearchParams({}, ["getPayloadFromInsight"])}`,
        );
    }, [identifier, index]);

    const getOnDemandInsightCardAction = () => {
        return (
            <StyledActionsContainer>
                <TextButton
                    onClick={() => void handleOnDemandInsightCardClick()}
                >
                    {CREATE_INSIGHT_TRIGGER_TEXT}
                </TextButton>
            </StyledActionsContainer>
        );
    };

    const getInsightFamilyCardAction = () => {
        const metadata = content.details ? content.details.metadata : undefined;
        const insightFamilyTrackingId =
            metadata?.[InsightFamilyMetadataAttributes.FAMILY_TRACKING_ID] ??
            undefined;
        const brandId =
            metadata?.[InsightFamilyMetadataAttributes.BRAND_ID] ?? undefined;
        const sidePanelDetailsForStandaloneInsights = buildSidePanelDetails(
            content,
            insightType,
        );

        return (
            <InsightFamilyCardAction
                identifier={identifier!}
                metadata={
                    metadata as Record<InsightFamilyMetadataAttributes, string>
                }
                insightFamilyTrackingId={insightFamilyTrackingId}
                details={sidePanelDetailsForStandaloneInsights}
                title={title!}
                index={index}
                brandId={brandId}
                familyName={cardHeader!}
            />
        );
    };

    const getMarketingNarrativeCardAction = () => {
        return (
            <MarketingNarrativeCardAction
                identifier={identifier!}
                index={index}
            />
        );
    };

    const getPitchedContentCardAction = () => {
        const pitchedContent = convertCardToPitchedContent(content);
        const pitchedContentDetails = getPitchedContentDetails(pitchedContent);

        return (
            <PitchedContentCardAction
                content={pitchedContent}
                details={pitchedContentDetails}
                isEditable={false}
                isSharable={false}
            />
        );
    };

    /**
     * InsightType to provider mapping defined in the model package here
     * https://tiny.amazon.com/esrdiz63/codeamazpackD16Gblob1293mode
     */
    const getAction = () => {
        switch (insightType) {
            case InsightType.CDB_TEMPLATE:
                return getOnDemandInsightCardAction();
            // TODO: Put this back after we are done with side panel
            // case InsightType.INSIGHT:
            //     return getInsightFamilyCardAction();
            case InsightType.MARKETING:
                return getMarketingNarrativeCardAction();
            case InsightType.PITCHED_CONTENT:
                return getPitchedContentCardAction();
            default:
                console.error(`Unsupported content type: ${insightType}`);
                return <></>;
        }
    };

    const getCardHeader = () => {
        switch (insightType) {
            case InsightType.INSIGHT:
                return InsightFamilyNameToLabelMap[
                    cardHeader as InsightFamilyName
                ];
            default:
                return cardHeader;
        }
    };

    return (
        <CardLayoutV3
            cardHeader={getCardHeader()!}
            insightType={insightType}
            insightTypeName={insightTypeLabelToValue[insightType] ?? undefined}
            primaryContext={primaryContext}
            secondaryContext={secondaryContext}
            additionalInfo={additionalInfo as KeyValue}
            lastModifiedDate={lastModifiedDate}
            tag={tag}
            actions={getAction()}
            dataTestId={identifier ?? ""}
            key={identifier}
            recommendation={
                "recommendation" in content ? content.recommendation : undefined
            }
        />
    );
};
